import { useCallback, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  COSMETICS,
  cosmeticWordKey,
  type CosmeticDef,
  type CosmeticPart,
} from '@il-colpo/meta/progress';
import type { EmoteId } from '@il-colpo/shared';
import type { Avatar } from '@il-colpo/shared/account';
import { BLUEPRINT_THEMES, type BlueprintTheme } from '@il-colpo/shared/design';
import { AvatarFigure } from '../../account/AvatarFigure.tsx';
import { getAccountStore, useAccount } from '../../account/accountStore.ts';
import { EmoteGlyph } from '../../game/emotes/EmoteGlyph.tsx';
import { CarPicture, type CarStyle } from '../../game/puzzles/Vehicle.tsx';
import { useLooseT, type LooseT } from '../../game/text/loose.ts';
import { ownedCosmetics } from '../../meta/owned.ts';
import { useSettings, useSettingsStore } from '../../state/settingsStore.ts';
import { useToastStore } from '../../state/toastStore.ts';
import { Badge, Button, Icon, Panel, ScreenFrame } from '../../ui/index.ts';
import { Failed, GatePanel, Page, useMetaGate, useProgress } from './common.tsx';
import styles from './Meta.module.css';

const PARTS: readonly CosmeticPart[] = [
  'hat',
  'mask',
  'coat',
  'rim',
  'suit',
  'blueprint',
  'car',
  'emote',
];

export { ownedCosmetics, reachedBy } from '../../meta/owned.ts';

function unlockText(def: CosmeticDef, tm: LooseT): string {
  const rule = def.unlock;
  switch (rule.kind) {
    case 'level':
      return tm('unlock.level', { level: rule.level });
    case 'trophy':
      return tm('unlock.trophy', { name: tm(`trophy.${rule.trophy}.name`) });
    case 'league':
      return tm('unlock.league', { league: tm(`leagues.${rule.league}`) });
  }
}

function Swatch({ theme }: { theme: BlueprintTheme }) {
  const t = BLUEPRINT_THEMES[theme];
  return (
    <span
      className={styles['swatch']}
      aria-hidden="true"
      style={{ background: t.paper, color: t.line, borderColor: t.line }}
    >
      <Icon name="map" size="2rem" />
    </span>
  );
}

function Card({
  def,
  avatar,
  owned,
  worn,
  onWear,
}: {
  def: CosmeticDef;
  avatar: Avatar | null;
  owned: boolean;
  worn: boolean;
  onWear: () => void;
}) {
  const { t } = useTranslation('meta');
  const tm = useLooseT('meta');
  const state = worn ? 'worn' : owned ? 'owned' : 'locked';
  return (
    <li className={styles['card']} data-state={state}>
      <div className={styles['preview']}>
        {def.part === 'blueprint' ? (
          <Swatch theme={def.value as BlueprintTheme} />
        ) : def.part === 'car' ? (
          <CarPicture style={def.value as CarStyle} />
        ) : def.part === 'emote' ? (
          <EmoteGlyph id={def.value as EmoteId} size="3.4rem" />
        ) : avatar ? (
          <AvatarFigure avatar={{ ...avatar, [def.part]: def.value }} size="4.5rem" />
        ) : null}
      </div>
      <h3 className={styles['cardTitle']}>{tm(`cosmetic.${cosmeticWordKey(def.id)}`)}</h3>
      {owned && def.part === 'emote' ? (
        <Badge tone="green">{t('collection.unlocked')}</Badge>
      ) : owned ? (
        <Button variant={worn ? 'secondary' : 'primary'} disabled={worn} onClick={onWear}>
          {worn ? t('collection.equipped') : t('collection.equip')}
        </Button>
      ) : (
        <>
          <Badge tone="neutral">
            <Icon name="lock" size="0.9rem" /> {t('collection.locked')}
          </Badge>
          <p className={styles['cardBody']}>{unlockText(def, tm)}</p>
        </>
      )}
    </li>
  );
}

/** `/collection`: every cosmetic there is, which ones the player has, how to get the others, and what they wear. */
export function CollectionScreen() {
  const { t } = useTranslation('meta');
  const tm = useLooseT('meta');
  const gate = useMetaGate();
  const progress = useProgress(gate === 'ready');
  const profile = useAccount((s) => s.profile);
  const theme = useSettings().game.blueprintTheme;
  const [failed, setFailed] = useState(false);

  const car = useSettings().game.escapeCar;
  const wearAvatar = useCallback(
    (part: Exclude<CosmeticPart, 'blueprint' | 'car' | 'emote'>, value: string) => {
      const current = getAccountStore().getState().profile;
      if (!current) return;
      setFailed(false);
      void getAccountStore()
        .getState()
        .updateProfile({ avatar: { ...current.avatar, [part]: value } })
        .then((r) => {
          if (r.ok) useToastStore.getState().push(t('collection.saved'), 'success');
          else setFailed(true);
        });
    },
    [t],
  );

  const wearTheme = (value: BlueprintTheme) => {
    useSettingsStore.getState().update((draft) => {
      draft.game.blueprintTheme = value;
    });
    useToastStore.getState().push(t('collection.saved'), 'success');
  };

  const wearCar = (value: CarStyle) => {
    useSettingsStore.getState().update((draft) => {
      draft.game.escapeCar = value;
    });
    useToastStore.getState().push(t('collection.saved'), 'success');
  };

  const owned = progress.data ? ownedCosmetics(progress.data) : null;
  const total = COSMETICS.length;
  return (
    <ScreenFrame title={t('collection.title')} backTo="/">
      <Page>
        <p className={styles['note']}>{t('collection.intro')}</p>
        {gate !== 'ready' ? <GatePanel gate={gate} /> : null}
        {gate === 'ready' && progress.status === 'loading' ? <GatePanel gate="loading" /> : null}
        {gate === 'ready' && progress.status === 'error' ? (
          <Failed onRetry={progress.reload} />
        ) : null}
        {owned ? (
          <>
            <Panel>
              <div className={styles['levelHead']}>
                {profile ? <AvatarFigure avatar={profile.avatar} size="5.5rem" /> : null}
                <div className={styles['levelText']}>
                  <span className={styles['levelNumber']}>
                    {t('collection.owned', {
                      owned: COSMETICS.filter((c) => owned.has(c.id)).length,
                      total,
                    })}
                  </span>
                  <Link to="/account/profile" className={styles['note']}>
                    {t('collection.toProfile')}
                  </Link>
                </div>
              </div>
            </Panel>
            {failed ? (
              <p className={styles['error']} role="alert">
                {t('collection.failed')}
              </p>
            ) : null}
            {PARTS.map((part) => (
              <section key={part} aria-label={tm(`parts.${part}`)}>
                <h2 className={styles['partTitle']}>{tm(`parts.${part}`)}</h2>
                {part === 'blueprint' ? (
                  <p className={styles['note']}>{t('collection.blueprintHint')}</p>
                ) : null}
                {part === 'car' ? (
                  <p className={styles['note']}>{t('collection.carHint')}</p>
                ) : null}
                {part === 'emote' ? (
                  <p className={styles['note']}>{t('collection.emoteHint')}</p>
                ) : null}
                <ul className={styles['cards']}>
                  {part === 'blueprint' ? (
                    <li
                      className={styles['card']}
                      data-state={theme === 'classic' ? 'worn' : 'owned'}
                    >
                      <div className={styles['preview']}>
                        <Swatch theme="classic" />
                      </div>
                      <h3 className={styles['cardTitle']}>{t('collection.classic')}</h3>
                      <Button
                        variant={theme === 'classic' ? 'secondary' : 'primary'}
                        disabled={theme === 'classic'}
                        onClick={() => {
                          wearTheme('classic');
                        }}
                      >
                        {theme === 'classic' ? t('collection.equipped') : t('collection.equip')}
                      </Button>
                    </li>
                  ) : null}
                  {part === 'car' ? (
                    <li className={styles['card']} data-state={car === 'sedan' ? 'worn' : 'owned'}>
                      <div className={styles['preview']}>
                        <CarPicture style="sedan" />
                      </div>
                      <h3 className={styles['cardTitle']}>{t('collection.classicCar')}</h3>
                      <Button
                        variant={car === 'sedan' ? 'secondary' : 'primary'}
                        disabled={car === 'sedan'}
                        onClick={() => {
                          wearCar('sedan');
                        }}
                      >
                        {car === 'sedan' ? t('collection.equipped') : t('collection.equip')}
                      </Button>
                    </li>
                  ) : null}
                  {COSMETICS.filter((c) => c.part === part).map((def) => {
                    const worn =
                      def.part === 'blueprint'
                        ? theme === def.value
                        : def.part === 'car'
                          ? car === def.value
                          : def.part === 'emote'
                            ? false
                            : profile?.avatar[def.part] === def.value;
                    return (
                      <Card
                        key={def.id}
                        def={def}
                        avatar={profile?.avatar ?? null}
                        owned={owned.has(def.id)}
                        worn={worn}
                        onWear={() => {
                          if (def.part === 'blueprint') wearTheme(def.value as BlueprintTheme);
                          else if (def.part === 'car') wearCar(def.value as CarStyle);
                          else if (def.part !== 'emote') wearAvatar(def.part, def.value);
                        }}
                      />
                    );
                  })}
                </ul>
              </section>
            ))}
          </>
        ) : null}
      </Page>
    </ScreenFrame>
  );
}
